import type { RouteBounds, RouteLineString } from './route-core';

export const MAX_ROUTE_WAYPOINTS = 20;

export type RouteUnitSystem = 'metric' | 'imperial';
export type RouteRoutingProfile = 'cycling-road' | 'foot-walking';

export type RouteCoordinate = Readonly<{
  latitude: number;
  longitude: number;
}>;

export type RouteWaypoint = RouteCoordinate &
  Readonly<{
    id: string;
  }>;

export type ComputedRoutePlan = Readonly<{
  bounds: RouteBounds;
  distanceM: number;
  durationSeconds: number | null;
  elevationGainM: number | null;
  geometry: RouteLineString;
  provider: 'openrouteservice' | 'straight-line' | 'valhalla';
  routingProfile: RouteRoutingProfile | null;
  waypoints: readonly RouteWaypoint[];
}>;

export function isRouteCoordinate(value: unknown): value is RouteCoordinate {
  if (!value || typeof value !== 'object') return false;
  const coordinate = value as Partial<RouteCoordinate>;
  return (
    Number.isFinite(coordinate.latitude) &&
    Number.isFinite(coordinate.longitude) &&
    coordinate.latitude! >= -90 &&
    coordinate.latitude! <= 90 &&
    coordinate.longitude! >= -180 &&
    coordinate.longitude! <= 180
  );
}

export function decodeGooglePolyline(encoded: string, precision = 5): RouteCoordinate[] {
  const coordinates: RouteCoordinate[] = [];
  let index = 0;
  let latitude = 0;
  let longitude = 0;

  while (index < encoded.length) {
    const latitudeDelta = decodePolylineValue(encoded, index);
    index = latitudeDelta.nextIndex;
    const longitudeDelta = decodePolylineValue(encoded, index);
    index = longitudeDelta.nextIndex;
    latitude += latitudeDelta.value;
    longitude += longitudeDelta.value;
    const scale = 10 ** precision;
    coordinates.push({ latitude: latitude / scale, longitude: longitude / scale });
  }

  return coordinates;
}

function decodePolylineValue(encoded: string, startIndex: number) {
  let result = 0;
  let shift = 0;
  let index = startIndex;
  let byte: number;

  do {
    if (index >= encoded.length) throw new Error('Invalid encoded route geometry.');
    byte = encoded.charCodeAt(index) - 63;
    index += 1;
    result |= (byte & 0x1f) << shift;
    shift += 5;
  } while (byte >= 0x20);

  return {
    nextIndex: index,
    value: result & 1 ? ~(result >> 1) : result >> 1,
  };
}

export function routeLineString(coordinates: readonly RouteCoordinate[]): RouteLineString {
  if (coordinates.length < 2) throw new Error('A route needs at least two points.');
  return {
    coordinates: coordinates.map(({ latitude, longitude }) => [longitude, latitude]),
    type: 'LineString',
  };
}

export function routeBounds(coordinates: readonly RouteCoordinate[]): RouteBounds {
  if (coordinates.length === 0) throw new Error('Route bounds need at least one point.');
  return coordinates.reduce<RouteBounds>(
    (bounds, coordinate) => ({
      maxLat: Math.max(bounds.maxLat, coordinate.latitude),
      maxLng: Math.max(bounds.maxLng, coordinate.longitude),
      minLat: Math.min(bounds.minLat, coordinate.latitude),
      minLng: Math.min(bounds.minLng, coordinate.longitude),
    }),
    {
      maxLat: coordinates[0].latitude,
      maxLng: coordinates[0].longitude,
      minLat: coordinates[0].latitude,
      minLng: coordinates[0].longitude,
    }
  );
}

export function straightLineRoutePlan(waypoints: readonly RouteWaypoint[]): ComputedRoutePlan {
  if (waypoints.length < 2) throw new Error('A route needs at least two points.');

  return {
    bounds: routeBounds(waypoints),
    distanceM: waypointDistanceM(waypoints),
    durationSeconds: null,
    elevationGainM: null,
    geometry: routeLineString(waypoints),
    provider: 'straight-line',
    routingProfile: null,
    waypoints,
  };
}

export function waypointDistanceM(coordinates: readonly RouteCoordinate[]) {
  let distanceM = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    distanceM += haversineDistanceM(coordinates[index - 1], coordinates[index]);
  }
  return distanceM;
}

export function haversineDistanceM(from: RouteCoordinate, to: RouteCoordinate) {
  const earthRadiusM = 6_371_000;
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitudeDelta = toRadians(to.latitude - from.latitude);
  const longitudeDelta = toRadians(to.longitude - from.longitude);
  const fromLatitude = toRadians(from.latitude);
  const toLatitude = toRadians(to.latitude);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(longitudeDelta / 2) ** 2;

  return earthRadiusM * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

export type RoutePosition = Readonly<{
  bearing: number;
  coordinate: RouteCoordinate;
}>;

export type RouteMilestone = RoutePosition &
  Readonly<{
    value: number;
  }>;

export function routePositionAtDistance(
  coordinates: readonly RouteCoordinate[],
  targetDistanceM: number
): RoutePosition | null {
  if (coordinates.length < 2 || targetDistanceM < 0) return null;

  let traversedM = 0;
  for (let index = 1; index < coordinates.length; index += 1) {
    const from = coordinates[index - 1];
    const to = coordinates[index];
    const segmentDistanceM = haversineDistanceM(from, to);
    if (segmentDistanceM === 0) continue;

    if (traversedM + segmentDistanceM >= targetDistanceM) {
      const ratio = Math.min(1, Math.max(0, (targetDistanceM - traversedM) / segmentDistanceM));
      return {
        bearing: routeBearing(from, to),
        coordinate: {
          latitude: from.latitude + (to.latitude - from.latitude) * ratio,
          longitude: from.longitude + (to.longitude - from.longitude) * ratio,
        },
      };
    }
    traversedM += segmentDistanceM;
  }

  const last = coordinates.at(-1)!;
  const previous = coordinates.at(-2)!;
  return { bearing: routeBearing(previous, last), coordinate: last };
}

export function routeMilestones(
  coordinates: readonly RouteCoordinate[],
  intervalM: number
): RouteMilestone[] {
  if (coordinates.length < 2 || intervalM <= 0) return [];
  const totalDistanceM = waypointDistanceM(coordinates);
  const milestones: RouteMilestone[] = [];

  for (let value = 1; value * intervalM < totalDistanceM; value += 1) {
    const position = routePositionAtDistance(coordinates, value * intervalM);
    if (position) milestones.push({ ...position, value });
  }
  return milestones;
}

export function routeDirectionMarkers(
  coordinates: readonly RouteCoordinate[],
  spacingM = 1000
): RoutePosition[] {
  if (coordinates.length < 2 || spacingM <= 0) return [];
  const totalDistanceM = waypointDistanceM(coordinates);
  const markers: RoutePosition[] = [];

  for (
    let targetDistanceM = spacingM / 2;
    targetDistanceM < totalDistanceM;
    targetDistanceM += spacingM
  ) {
    const position = routePositionAtDistance(coordinates, targetDistanceM);
    if (position) markers.push(position);
  }
  return markers;
}

function routeBearing(from: RouteCoordinate, to: RouteCoordinate) {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const toDegrees = (radians: number) => (radians * 180) / Math.PI;
  const fromLatitude = toRadians(from.latitude);
  const toLatitude = toRadians(to.latitude);
  const longitudeDelta = toRadians(to.longitude - from.longitude);
  const y = Math.sin(longitudeDelta) * Math.cos(toLatitude);
  const x =
    Math.cos(fromLatitude) * Math.sin(toLatitude) -
    Math.sin(fromLatitude) * Math.cos(toLatitude) * Math.cos(longitudeDelta);
  return (toDegrees(Math.atan2(y, x)) + 360) % 360;
}

export function elevationGain(elevations: readonly number[]): number | null {
  if (elevations.length < 2) return null;
  let gain = 0;
  for (let index = 1; index < elevations.length; index += 1) {
    const delta = elevations[index] - elevations[index - 1];
    if (delta > 0) gain += delta;
  }
  return Math.round(gain);
}

export function moveWaypoint(
  waypoints: readonly RouteWaypoint[],
  fromIndex: number,
  toIndex: number
): RouteWaypoint[] {
  if (fromIndex === toIndex) return [...waypoints];
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= waypoints.length ||
    toIndex >= waypoints.length
  ) {
    return [...waypoints];
  }
  const next = [...waypoints];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export function formatPlannerDistance(distanceM: number, units: RouteUnitSystem) {
  if (units === 'imperial') {
    const miles = distanceM / 1609.344;
    return miles < 0.1 ? `${Math.round(distanceM * 3.28084)} FT` : `${miles.toFixed(2)} MI`;
  }
  return distanceM < 1000 ? `${Math.round(distanceM)} M` : `${(distanceM / 1000).toFixed(2)} KM`;
}

export function formatPlannerDistanceSignificant(
  distanceM: number,
  units: RouteUnitSystem
): Readonly<{ unit: 'KM' | 'MI'; value: string }> {
  const distance = units === 'imperial' ? distanceM / 1609.344 : distanceM / 1000;
  if (distance === 0) return { unit: units === 'imperial' ? 'MI' : 'KM', value: '0.00' };

  const digitsBeforeDecimal = Math.floor(Math.log10(Math.abs(distance))) + 1;
  const decimalPlaces = Math.max(0, 3 - digitsBeforeDecimal);
  return {
    unit: units === 'imperial' ? 'MI' : 'KM',
    value: distance.toFixed(decimalPlaces),
  };
}

export function formatPlannerElevation(elevationM: number | null, units: RouteUnitSystem) {
  if (elevationM == null) return '—';
  return units === 'imperial'
    ? `${Math.round(elevationM * 3.28084)} FT`
    : `${Math.round(elevationM)} M`;
}
